import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { PapiBadge, PapiUser } from '@wix/da-papi-types';
import Badge from '@wix/da-ds/pkg/Badge';
import { showBadgeInfoModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { getBiDataForBadge } from '@wix/da-shared-react/pkg/utils/badges';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import { useTranslation } from 'react-i18next';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';

import s from './CategoryBadgeBox.scss';

export interface Props {
  badge: PapiBadge;
  className?: string;
  profileOwnerUser: PapiUser;
}

const CategoryBadgeBox: FC<Props> = ({
  profileOwnerUser,
  badge,
  className,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector(state => getCurrentUser(state));
  const isOwnPage = currentUser.userId === profileOwnerUser.userId;
  const isPast = useMemo(() => {
    const now = new Date();
    const end = badge.campaignEnd;
    return end ? badge.campaignEnd.localeCompare(now.toISOString()) < 0 : false;
  }, [badge]);
  const shouldShow = useMemo(() => {
    return !badge.isUnearned || (isOwnPage && !isPast);
  }, [badge, isOwnPage, isPast]);

  const showInfo = useCallback(() => {
    if (badge.id) {
      dispatch(
        showBadgeInfoModal(
          profileOwnerUser.username,
          badge.id,
          undefined,
          badge.isUnearned
        )
      );
    } else {
      dispatch(
        showBadgeInfoModal(
          profileOwnerUser.username,
          undefined,
          badge.typeId,
          badge.isUnearned
        )
      );
    }
  }, [dispatch, profileOwnerUser, badge]);

  const renderContent = () => {
    const title = badge.title;
    const given = badge.given || 0;
    const received = badge.received || 0;

    const showCountInTitle = given > 0 || received > 1;
    let countStr = '';
    if (given > 0) {
      countStr = `${shortenNumber(given, 0)} ${t('widgets.badges.collections.badgecount.given')}`;
      if (received > 0) {
        countStr = `${countStr}\u00A0\u00A0\u00A0${shortenNumber(received, 0)} ${t('widgets.badges.collections.badgecount.received')}`;
      }
    } else {
      if (received > 1) {
        countStr = `${shortenNumber(received, 0)} ${t('widgets.badges.collections.badgecount.received')}`;
      }
    }

    return (
      <>
        <Badge className={s['badgeIcon']} badge={badge} size={68} />
        <div className={s['badge-title']} title={title}>
          {title}
        </div>
        {showCountInTitle && <div className={s['badge-count']}>{countStr}</div>}
      </>
    );
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <PlainButtonOrLink
      className={classnames(
        s['root'],
        className,
        badge.isUnearned && s['unearned']
      )}
      onClick={showInfo}
      biData={getBiDataForBadge(badge)}
    >
      {renderContent()}
    </PlainButtonOrLink>
  );
};
CategoryBadgeBox.displayName = 'CategoryBadgeBox';
export default CategoryBadgeBox;
